import styled, { css } from 'styled-components';

import { SPACING } from '~/common/constants/spacing';
import { BREAKS } from '~/common/constants/breaks';

export const PublicLayoutContainer = styled.div`
  min-height: 100%;
  width: 100%;
  display: flex;
  background: ${({ theme }) => theme.neutral[950]};
`;

/* ── Painel de marca (desktop) ───────────────────────────────────────────
   Whitelabel: todo o painel é pintado a partir da cor primária da
   instância (theme.primary_*), sem nenhuma cor de produto chumbada. */
export const BrandPanel = styled.aside`
  position: relative;
  overflow: hidden;
  flex: 1.05;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${SPACING._64};
  background: linear-gradient(
    168deg,
    ${({ theme }) => theme.primary_darker} 0%,
    ${({ theme }) => theme.primary_header} 130%
  );

  @media (max-width: 1023px) {
    display: none;
  }
`;

export const BrandGrid = styled.div`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.045) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(
    ellipse 95% 85% at 32% 42%,
    #000 25%,
    transparent 100%
  );
  -webkit-mask-image: radial-gradient(
    ellipse 95% 85% at 32% 42%,
    #000 25%,
    transparent 100%
  );
  pointer-events: none;
`;

export const BrandOrb = styled.div<{ $variant: 1 | 2 }>`
  position: absolute;
  width: 480px;
  height: 480px;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
  opacity: 0.55;
  background: ${({ theme, $variant }) =>
    $variant === 1
      ? `radial-gradient(circle, ${theme.primary}66 0%, transparent 65%)`
      : 'radial-gradient(circle, rgba(255, 255, 255, 0.14) 0%, transparent 65%)'};
  ${({ $variant }) =>
    $variant === 1
      ? css`
          top: -140px;
          right: -160px;
          animation: orbDrift 16s ease-in-out infinite alternate;
        `
      : css`
          bottom: -180px;
          left: -140px;
          animation: orbDrift 22s ease-in-out infinite alternate-reverse;
        `}

  @keyframes orbDrift {
    from {
      transform: translate(0, 0) scale(1);
    }
    to {
      transform: translate(-48px, 36px) scale(1.12);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const BrandRings = styled.svg`
  position: absolute;
  right: -130px;
  bottom: -130px;
  width: 440px;
  height: 440px;
  pointer-events: none;
  opacity: 0.6;
`;

export const BrandContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING._20};
  animation: brandIn 0.6s ease-out both;

  @keyframes brandIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const BrandEyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: ${SPACING._10};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.62);

  &::before {
    content: '';
    width: 26px;
    height: 1px;
    background: rgba(255, 255, 255, 0.5);
  }
`;

export const BrandHeadline = styled.h2`
  font-size: clamp(28px, 2.7vw, 38px);
  font-weight: 600;
  line-height: 1.18;
  letter-spacing: -0.02em;
  color: #ffffff;
`;

export const BrandList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${SPACING._12};
  margin-top: ${SPACING._8};
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: ${SPACING._10};
    font-size: 15px;
    color: rgba(255, 255, 255, 0.82);

    svg {
      flex-shrink: 0;
      color: rgba(255, 255, 255, 0.85);
    }
  }
`;

/* ── Lado do formulário ──────────────────────────────────────────────── */
export const FormSide = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  min-height: 100%;

  /* leve tintura da cor da instância no topo — funciona em light e dark */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 90% 55% at 50% -10%,
      ${({ theme }) => `${theme.primary}14`} 0%,
      transparent 65%
    );
    pointer-events: none;
  }
`;

export const Wrapper = styled.div`
  position: relative;
  z-index: 1;
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${SPACING._48} ${SPACING._32};

  @media ${BREAKS.MOBILE} {
    padding: ${SPACING._32} ${SPACING._20};
  }

  @media ${BREAKS.TABLET_SMALL_NOTEBOOK} {
    padding: ${SPACING._48} ${SPACING._24};
  }
`;

export const Content = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const LoginCard = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 440px;
  background: ${({ theme }) => theme.neutral[1000]};
  border: 1px solid ${({ theme }) => theme.neutral[900]};
  border-radius: 24px;
  padding: ${SPACING._48} ${SPACING._40};
  display: flex;
  flex-direction: column;
  gap: ${SPACING._32};
  box-shadow: 0 24px 64px -16px rgba(0, 0, 0, 0.18);
  animation: cardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;

  /* filete superior com a cor da instância */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.primary},
      ${({ theme }) => theme.primary_header}
    );
  }

  @keyframes cardIn {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media ${BREAKS.MOBILE} {
    padding: ${SPACING._32} ${SPACING._20};
    border-radius: 20px;
  }
`;

export const LogoSlot = styled.div`
  display: flex;
  justify-content: center;

  img {
    max-width: 100%;
    height: auto;
  }
`;

export const Footer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-flow: wrap;
  gap: ${SPACING._16};
  padding-top: ${SPACING._24};
`;
